import dom from '../utils/dom';
import FormControl from './controls/FormControl';
import type { FormControlElement } from './controls/FormControl';
import IconSprites from '../utils/IconSprites';
import SvgIcon from '../utils/SvgIcon';

function findOrCreatErrorHelpBlock(controlId: string) {
  const id = `tn-error-help-block-${controlId}`;

  const $el = document.getElementById(id);

  if ($el) {
    return $el as HTMLElement;
  }

  return dom.makeElement('div', { id, class: 'help-block' }) as HTMLElement;
}

function findOrCreateRequiredIndicator($el: Element) {
  let $indicator = dom.findFrom($el, '.tn-required-field') as HTMLElement;

  if (!$indicator) {
    $indicator = dom.makeElement('span', { 'aria-hidden': true, class: 'tn-required-field' });
    $indicator.innerText = 'Required';
  }

  return $indicator;
}

class ControlGroup {
  $group: HTMLElement;

  id: string;

  $requiredIndicator: HTMLElement;

  $errorHelpBlock: HTMLElement;

  input: FormControl<FormControlElement>;

  order: number;

  constructor(id: string, $group: HTMLElement, input:FormControl<FormControlElement>, order = -1) {
    this.id = id;
    this.$group = $group;
    this.input = input;
    this.order = order;

    this.startListening();
    this.$errorHelpBlock = findOrCreatErrorHelpBlock(this.id);
    this.$requiredIndicator = findOrCreateRequiredIndicator($group);
  }

  clearErrors() {
    this.$group.classList.remove('has-error');

    if (this.$errorHelpBlock.parentElement) {
      dom.remove(this.$errorHelpBlock);
    }

    dom.findAllFrom(this.$group, '.control-label--with-icon')
      .forEach(($el) => {
        $el.classList.remove('control-label--with-icon');
        dom.findAllFrom($el, '.control-label__icon-error').forEach(($icon) => dom.remove($icon));
      });

    this.input.isInvalid = false;
  }

  showError(message: string) {
    this.$group.classList.add('has-error');

    this.$errorHelpBlock.innerHTML = message;

    this.$group.appendChild(this.$errorHelpBlock);

    dom.findAllFrom(this.$group, '.control-label:not(.control-label--with-icon)')
      .forEach(($label) => {
        $label.classList.add('control-label--with-icon');
        $label.prepend(SvgIcon.getElement(IconSprites.XCircleFill, 'control-label__icon-error'));
      });

    this.input.isInvalid = true;
  }

  setIsRequired(value: boolean) {
    this.input.isRequired = value;

    const $indicator = this.$requiredIndicator;

    if (value) {
      if (!$indicator.parentElement) {
        dom.findFrom(this.$group, '.control-label')?.append($indicator);
      }
    } else {
      if ($indicator.parentElement) {
        dom.remove(this.$requiredIndicator);
      }
    }
  }

  show() {
    dom.unhide(this.$group);
  }

  hide() {
    dom.hide(this.$group);
  }

  startListening() {
    this.input.startListening();
  }

  stopListening() {
    this.input.stopListening();
  }
}

export default ControlGroup;
